import { useQuery } from "@tanstack/react-query"
import { ArrowLeft, ArrowRight, X } from "lucide-react"

import RenderLocation from "@/components/RenderLocation.tsx"
import RenderUnit from "@/components/RenderUnit.tsx"
import { Button } from "@/components/ui/button.tsx"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx"
import type { AggregationMode } from "@/types/dataPointTypes.ts"

import { getDataPoint } from "../../api/getDataPoint.ts"
import AggregationModeSelector from "../AggregationModeSelector.tsx"

export default function DataPointHeader({
  id,
  index,
  setIndex,
  last,
  onRemove,
  aggMode,
  setAggMode,
}: {
  id: number
  index: number
  setIndex: (i: number) => void
  last: boolean
  onRemove: () => void
  aggMode: AggregationMode
  setAggMode: (a: AggregationMode) => void
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["dataPoint", { id }],
    queryFn: () => getDataPoint(id),
  })
  if (isPending) return <></>
  if (isError) return <></>

  return (
    <Card className="h-[200px] justify-between">
      <CardHeader>
        <Tooltip>
          <TooltipTrigger asChild>
            <CardTitle className="text-wrap max-w-3/4 leading-snug line-clamp-2 cursor-default">
              {data.name}{" "}
              <i>
                (<RenderUnit id={data.unitId} />)
              </i>
            </CardTitle>
          </TooltipTrigger>
          <TooltipContent>
            {data.name}{" "}
            <i>
              (<RenderUnit id={data.unitId} />)
            </i>
          </TooltipContent>
        </Tooltip>
        <CardDescription>
          <RenderLocation id={data.locationId} />
        </CardDescription>
        <CardAction>
          <Button variant="ghost" type="button" onClick={onRemove}>
            <X />
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between mb-3 mt-[-10px]">
          <Button
            variant="ghost"
            type="button"
            disabled={index == 0}
            onClick={() => setIndex(index - 1)}
          >
            <ArrowLeft />
          </Button>
          <Button variant="ghost" type="button" disabled={last} onClick={() => setIndex(index + 1)}>
            <ArrowRight />
          </Button>
        </div>
        <AggregationModeSelector value={aggMode} onChange={setAggMode} />
      </CardContent>
    </Card>
  )
}
